/**
 * Градусы Фаренгейта в градусы Цельсия
 * Градусы Фаренгейта в градусы Цельсия
 * ====================================
 * TODO Сравнение температурных шкал: https://meteoinfo.ru/t-scale#:~:text=%D0%A8%D0%BA%D0%B0%D0%BB%D0%B0%20%D0%A4%D0%B0%D1%80%D0%B5%D0%BD%D0%B3%D0%B5%D0%B9%D1%82%D0%B0&text=%D0%92%20%D0%BD%D0%B0%D1%81%D1%82%D0%BE%D1%8F%D1%89%D0%B5%D0%B5%20%D0%B2%D1%80%D0%B5%D0%BC%D1%8F%20%D0%BF%D1%80%D0%B8%D0%BD%D1%8F%D1%82%D0%BE%20%D1%81%D0%BB%D0%B5%D0%B4%D1%83%D1%8E%D1%89%D0%B5%D0%B5,%D0%B8%D0%BC%D0%B5%D0%B5%D1%82%20%D1%82%D0%B5%D0%BC%D0%BF%D0%B5%D1%80%D0%B0%D1%82%D1%83%D1%80%D1%83%20%2B32%20%C2%B0F.
 * TODO Формулу заменить на свою картинку или как-там их отображать
 */
import { LoaderFunctionArgs, MetaFunction, useLoaderData } from "react-router";
import { useTranslation } from "react-i18next";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import invariant from "tiny-invariant";
import TemperatureConverter from "~/components/TemperatureConverter";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";
import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";

const CATEGORY = "temperature";
const TKEY = "calculators.temperature.celsius_to_fahrenheit";
const ROUTE_ENDING = "celsius-to-fahrenheit";
const LAST_UPDATED = new Date("2025-02-11T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov,
    ),
  ];

  addNoindexMeta(meta, data.meta.shouldIndex);

  return meta;
}

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "tr", "it", "pl", "ar", "th"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  const locale = getLocale(request.url);

  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number)) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = celsiusToFahrenheit(number);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }

  const title = number !== null
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` },
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex: ShouldIndexMeta = {
    googlebot: number === null,
    yandex: false,
    bingbot: false,
  };

  if (
    // Root page
    number === null
    || number === -273.15
    // 0 - 400
    || (number >= 0 && number <= 400 && number % 1 === 0)
    || (number <= 1000 && number % 25 === 0)
    || (number <= 10000 && number % 500 === 0)
    || (number <= 900000 && number % 10000 === 0)
  ) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    number,
    result,
    error,
    meta: {
      locale,
      title,
      h1:  number === null ? t(`${TKEY}.title`) : t(`${TKEY}.question_n`, { count: number }),
      description: number === null ? t(`${TKEY}.description`) : null,
      breadcrumbs,
      shouldIndex,
    }
  };
};
function celsiusToFahrenheit(celsius: number): number {
  const fahrenheit = (celsius * 9 / 5) + 32;
  return parseFloat(fahrenheit.toFixed(2));
}

export default function Index() {
  const {
    meta,
    number,
    result,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");

  const commonValues = [];


  for (let i = 0; i <= 100; i += 1) {
    commonValues.push(i);
  }

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.h1}
          description={meta.description}
        />

        {result !== null && number !== null && !error ? (
          <Result
            color="positive"
          >
            <H3 className="first-letter:capitalize">{t("answer")}:</H3>

            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.answer`, { from: number, to: result })}
            </div>

            <div className="mt-3 mb-8 text-center">
              {number} °C = <strong className="mt-8 text-3xl first-letter:capitalize">{result} °F</strong>
            </div>
          </Result>
        ) : null}

        {error ?
          <Result color="negative">
            <p>{error}</p>
          </Result>
          : null
        }

        <ToolForm>
          <TemperatureConverter
            valueA={number ?? 0}
            unitA={"C"}
            unitB={"F"}
          />
        </ToolForm>
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          {number !== null ? <>
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert_n`, { count: number })}</H2>
              <p>{t(`${TKEY}.info_h_how_to_convert_n_formula`)}</p>
              <div className="flex justify-center mb-4">
                <div className="bg-gray-300 rounded-md p-6 w-[200px] flex justify-center">
                  <img
                    src="https://wikimedia.org/api/rest_v1/media/math/render/svg/2ae81b7dad40aa49e72aa5ba88ec7d118ebc483f"
                    alt="Conversion Formula (wikimedia)" />
                </div>
              </div>
              <p>{t(`${TKEY}.info_h_how_to_convert_n_lets_count`, { count: number })}</p>
              <p className="text-center">
                {number} * 9 / 5 + 32<br />
                = <br />
                {number * 9} / 5 + 32<br />
                = <br />
                {number * 9 / 5} + 32<br />
                = <br />
                {result}
              </p>
            </AdditionalInfoSection>
          </> : null}

          {number ? null : <>
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_definition`)}</H2>
              <p>{t(`${TKEY}.info_p_definition`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
              <p>{t(`${TKEY}.info_p_how_to_convert`)}</p>
              <div className="bg-gray-300 rounded-md p-6 w-[200px]">
                <img
                  src="https://wikimedia.org/api/rest_v1/media/math/render/svg/2ae81b7dad40aa49e72aa5ba88ec7d118ebc483f"
                  alt="Conversion Formula (wikimedia)" />
              </div>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_absolute_null`)}</H2>
              <p>{t(`${TKEY}.info_p_absolute_null`)}</p>
            </AdditionalInfoSection>

            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
              <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
              <div className="md:columns-2">
                <table
                  className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
                  <thead>
                  <tr>
                    <th className="font-bold px-3 py-2 border-b border-r border-gray-400">°C</th>
                    <th className="font-bold px-3 py-2 border-b border-gray-400">°F</th>
                  </tr>
                  </thead>
                  <tbody>
                  {commonValues.map((number) => (
                    <tr key={number} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                      <td className="px-3 py-2 border-b border-r border-gray-400">{number}</td>
                      <td className="px-3 py-2 border-b border-gray-400">{celsiusToFahrenheit(number)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>
          </>}
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>
  );
}


